import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaCode, FaServer, FaShieldAlt, FaCloudUploadAlt } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { theme } from '../../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary};
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Highlight = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(1, 191, 113, 0.1);
  }
`;

const CardIcon = styled.div`
  color: #01bf71;
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CardText = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const FeaturesList = styled.div`
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FeatureIcon = styled.div`
  color: #01bf71;
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: #fff;
`;

const ProcessSection = styled.div`
  margin-bottom: 80px;
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ProcessStep = styled.div`
  display: flex;
  gap: 20px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  background: #01bf71;
  color: #010606;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #01bf71, #018f55);
  border-radius: 10px;
  padding: 60px;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const CTATitle = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: transparent;
  border: 2px solid ${theme.colors.secondary};
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
`;

const SoftwareDevelopment = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Custom Software Development Services | Enterprise Solutions | Afrotech Group</title>
        <meta name="description" content="Professional software development services for businesses and enterprises. Custom solutions, web applications, and enterprise software built with cutting-edge technology. Contact Afrotech Group today!" />
        <meta name="keywords" content="software development, custom software, enterprise solutions, web applications, business software, Afrotech Group" />
      </Helmet>
      
      <ContentWrapper>
        <PageHeader>
          <Title>Custom <Highlight>Software Development</Highlight> Solutions</Title>
          <Subtitle>
            Building powerful, scalable, and secure software solutions tailored to your business requirements
          </Subtitle>
        </PageHeader>
        
        <Section>
          <SectionTitle>Our Software Development Services</SectionTitle>
          <Grid>
            <Card>
              <CardIcon><FaCode /></CardIcon>
              <CardTitle>Custom Software Solutions</CardTitle>
              <CardText>
                Bespoke software applications designed specifically for your business processes and requirements.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Tailored to your workflow</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Scalable architecture</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Integration with existing systems</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaServer /></CardIcon>
              <CardTitle>Enterprise Applications</CardTitle>
              <CardText>
                Robust enterprise-grade software that streamlines operations and enhances productivity.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>ERP and CRM solutions</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Business process automation</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Data analytics and reporting</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaShieldAlt /></CardIcon>
              <CardTitle>Secure Applications</CardTitle>
              <CardText>
                Software built with security at its core, protecting your sensitive data and systems.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>POPIA compliance</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Secure authentication systems</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Data encryption and protection</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaCloudUploadAlt /></CardIcon>
              <CardTitle>Cloud-Based Solutions</CardTitle>
              <CardText>
                Scalable cloud applications that provide flexibility and accessibility from anywhere.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>AWS, Azure, and GCP deployment</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Microservices architecture</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Serverless computing options</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <Section>
          <SectionTitle>Technologies We Use</SectionTitle>
          <Grid>
            <Card>
              <CardTitle>Frontend Development</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>React.js</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Angular</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Vue.js</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>Backend Development</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Node.js</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Python</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Java & Spring Boot</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>Database Technologies</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>PostgreSQL</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>MongoDB</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>MySQL</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardTitle>DevOps & Infrastructure</CardTitle>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Docker & Kubernetes</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>CI/CD Pipelines</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Infrastructure as Code</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <ProcessSection>
          <SectionTitle>Our Software Development Process</SectionTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepTitle>Requirements Analysis</StepTitle>
                <StepDescription>
                  We work closely with you to understand your business needs, objectives, and technical requirements to create a comprehensive development plan.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepTitle>Design & Architecture</StepTitle>
                <StepDescription>
                  Our architects design a robust, scalable, and secure software architecture that aligns with your business goals and technical requirements.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepTitle>Development</StepTitle>
                <StepDescription>
                  Using agile methodologies, our development team builds your software with regular sprints, demos, and feedback cycles to ensure alignment with your vision.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepTitle>Quality Assurance</StepTitle>
                <StepDescription>
                  Our QA team conducts thorough testing, including functional, performance, security, and user acceptance testing to ensure a robust final product.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepTitle>Deployment</StepTitle>
                <StepDescription>
                  We handle the deployment process, ensuring a smooth transition to production with minimal disruption to your operations.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>6</StepNumber>
              <StepContent>
                <StepTitle>Maintenance & Support</StepTitle>
                <StepDescription>
                  Our team provides ongoing maintenance, support, and updates to keep your software running optimally and evolving with your business needs.
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Build Your Custom Software Solution?</CTATitle>
          <CTAText>
            Contact our software development team today for a free consultation and quote. Let's create software that transforms your business.
          </CTAText>
          <CTAButton to="/contact">Start Your Project</CTAButton>
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SoftwareDevelopment;
